import * as React from "react";
import { Button } from "@material-ui/core";
import Visibility from "@material-ui/icons/Visibility";
import gql from "graphql-tag";

import { User } from "../../userTypes";
import { apolloClient } from "app/utils/graphQlClients";

type Props = {
  user: User;
};

type State = {
  loading: boolean;
};

const ID_PHOTO_QUERY = gql`
  query IdPhoto($external_reference: ID) {
    user(external_reference: $external_reference) {
      external_reference
      latest_id_photo_url
    }
  }
`;

export default class ManualVerificationViewIdPhotoButton extends React.Component<
  Props,
  State
> {
  state = {
    loading: false,
  };

  viewIdPhoto = async () => {
    const { external_reference } = this.props.user;

    // Without an external reference we cannot look up the photo, so avoid
    // firing the query or opening a blank tab.
    if (!external_reference) {
      alert("This user has no external reference, so the ID photo cannot be retrieved.");
      return;
    }

    // Open the tab synchronously as part of the click handler so that browsers
    // do not treat the later, asynchronous navigation as a blocked popup.
    const newTab = window.open("", "_blank");

    // If the popup was blocked, opening it again after the await would also be
    // blocked, so bail out and let the user know instead of silently failing.
    if (!newTab) {
      alert("Please allow popups for this site to view the ID photo.");
      return;
    }

    // Sever the opener reference so the new tab cannot access this window via
    // window.opener (reverse tabnabbing) or leak the referrer.
    newTab.opener = null;

    this.setState({ loading: true });

    try {
      const result = await apolloClient.query({
        query: ID_PHOTO_QUERY,
        // Signed URLs are sensitive and short-lived, so never persist them in
        // the Apollo cache.
        fetchPolicy: "no-cache",
        variables: {
          external_reference,
        },
      });

      const url =
        result && result.data && result.data.user
          ? result.data.user.latest_id_photo_url
          : null;

      if (url) {
        newTab.location.href = url;
      } else {
        newTab.close();
        alert("No ID photo is available for this user.");
      }
    } catch (error) {
      newTab.close();
      alert("Failed to fetch the ID photo. Please try again.");
    } finally {
      this.setState({ loading: false });
    }
  };

  render() {
    return (
      <Button
        color="default"
        variant="contained"
        className="button no-margin"
        onClick={this.viewIdPhoto}
        disabled={this.state.loading || !this.props.user.external_reference}
      >
        {this.state.loading ? "Loading..." : "View ID photo"}
        <Visibility
          style={{
            marginLeft: "10px",
          }}
        />
      </Button>
    );
  }
}
